import every from 'lodash/every';
import find from 'lodash/find';
import map from 'lodash/map';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import type { GetterTree } from 'vuex';

import { PREFERRED_COUNTRIES } from '@/consts/locales.const';
import type { ClientData, ClientDocument, ClientDrivingLicence, ClientIdentification, ClientPersonalData } from '@api/api.model';
import { DOCUMENT_TYPE } from '@consts/docs.const';

import type { State } from '../interfaces/state';
import type { BookingDetails, BrokerState, Country } from './interfaces';

export enum BrokerGetters {
  GetLocale = 'Broker:GET_LOCALE',
  GetClientData = 'Broker:GET_CLIENT_DATA',
  GetClientDocument = 'Broker:GET_CLIENT_DOCUMENT',
  GetClientPersonalData = 'Broker:GET_CLIENT_PERSONAL_DATA',
  GetClientDrivingLicenceData = 'Broker:GET_CLIENT_DRIVING_LICENCE_DATA',
  GetClientIdentificationData = 'Broker:GET_CLIENT_IDENTIFICATION_DATA',
  HasRequiredDocuments = 'Broker:HAS_REQUIRED_DOCUMENTS',
  HasOptionalDocuments = 'Broker:HAS_OPTIONAL_DOCUMENTS',
  IsNIF = 'Broker:IS_NIF',
  IsPassport = 'Broker:IS_PASSPORT',
  OnlyMandatoryFieldsMissing = 'Broker:ONLY_MANDATORY_FIELDS_MISSING',
  GetSortedCountries = 'Broker:GET_SORTED_COUNTRIES',
  GetValidationType = 'Broker:GET_VALIDATION_TYPE',
  GetSelectedCountry = 'Broker:GET_SELECTED_COUNTRY',
  GetDocumentsForValidation = 'Broker:GET_DOCUMENTS_FOR_VALIDATION',
  GetBookingDetails = 'Broker:GET_BOOKING_DETAILS',
  IsBookingReady = 'Broker:IS_BOOKING_READY',
}

export interface BrokerGetterData {
  [BrokerGetters.GetLocale]: (state: BrokerState) => string;
  [BrokerGetters.GetClientData]: (state: BrokerState) => ClientData;
  [BrokerGetters.GetClientDocument]: (state: BrokerState) => ClientDocument;
  [BrokerGetters.GetClientPersonalData]: (state: BrokerState) => ClientPersonalData;
  [BrokerGetters.GetClientDrivingLicenceData]: (state: BrokerState) => ClientDrivingLicence;
  [BrokerGetters.GetClientIdentificationData]: (state: BrokerState) => ClientIdentification;
  [BrokerGetters.HasRequiredDocuments]: (state: BrokerState) => boolean;
  [BrokerGetters.HasOptionalDocuments]: (state: BrokerState) => boolean;
  [BrokerGetters.IsNIF]: (state: BrokerState) => boolean;
  [BrokerGetters.IsPassport]: (state: BrokerState) => boolean;
  [BrokerGetters.OnlyMandatoryFieldsMissing]: (state: BrokerState) => boolean;
  [BrokerGetters.GetSortedCountries]: (state: BrokerState) => Country[];
  [BrokerGetters.GetValidationType]: (state: BrokerState) => string;
  [BrokerGetters.GetSelectedCountry]: (state: BrokerState) => string;
  [BrokerGetters.GetDocumentsForValidation]: (state: BrokerState) => string[];
  [BrokerGetters.GetBookingDetails]: (state: BrokerState) => BookingDetails;
  [BrokerGetters.IsBookingReady]: (state: BrokerState) => boolean;
}

export const getters: GetterTree<BrokerState, State> & BrokerGetterData = {
  [BrokerGetters.GetLocale]: (state) => state.data.locale,
  [BrokerGetters.GetClientData]: (state) => state.client.clientData || ({} as ClientData),
  [BrokerGetters.GetClientDocument]: (state) => state.client.clientDocument || ({} as ClientDocument),
  [BrokerGetters.GetClientPersonalData]: (state) => {
    const { clientData } = state.client;
    const { name, surname, nationality, address, postalCode, city, province, country, gender, phoneNumber, birthDay } = clientData;

    return { name, surname, nationality, address, postalCode, city, province, country, gender: gender?.toString(), phoneNumber, birthDay };
  },
  [BrokerGetters.GetClientIdentificationData]: (state) => {
    const { clientData } = state.client;
    const { idNumber, passport, nifExpirationDate, passportExpirationDate } = clientData;

    return { idNumber, passport, nifExpirationDate, passportExpirationDate };
  },
  [BrokerGetters.GetClientDrivingLicenceData]: (state) => {
    const { clientData } = state.client;
    const { licenseId, licenseType, licenseExpeditionCountry, drivingLicenseExpirationDate } = clientData;

    return { licenseId, licenseType, licenseExpeditionCountry, drivingLicenseExpirationDate };
  },
  [BrokerGetters.HasRequiredDocuments]: (state) => {
    const { documentRequired } = state.client.clientDocument;
    return documentRequired.some((element) => element === DOCUMENT_TYPE.nif || element === DOCUMENT_TYPE.passport);
  },
  [BrokerGetters.HasOptionalDocuments]: (state) => {
    const { optionalDocuments } = state.client.clientDocument;
    return optionalDocuments.some((element) => element === DOCUMENT_TYPE.nif || element === DOCUMENT_TYPE.passport);
  },
  [BrokerGetters.IsNIF]: (state) => {
    const { documentRequired, optionalDocuments } = state.client.clientDocument;
    return [...documentRequired, ...optionalDocuments].some((element) => element === DOCUMENT_TYPE.nif);
  },
  [BrokerGetters.IsPassport]: (state) => {
    const { documentRequired, optionalDocuments } = state.client.clientDocument;
    return [...documentRequired, ...optionalDocuments].some((element) => element === DOCUMENT_TYPE.passport);
  },
  [BrokerGetters.OnlyMandatoryFieldsMissing]: (state) => state.client.clientDocument.onlyMandatoryFieldsMissing,
  [BrokerGetters.GetSortedCountries]: (state) => {
    const { countries } = state.data;
    const INDETERMINATE_CODE = '0';
    const filteredCountries = reject(countries, ({ code }) => code.trim() === INDETERMINATE_CODE);
    const preferredCountries = map(PREFERRED_COUNTRIES, (code) => find(filteredCountries, ['code', code])!);
    const otherCountries = reject(filteredCountries, ({ code }) => PREFERRED_COUNTRIES.includes(code));
    const sortedCountries = sortBy(otherCountries, 'name');
    return [...preferredCountries, ...sortedCountries];
  },
  [BrokerGetters.GetValidationType]: (state) => state.data.validationType,
  [BrokerGetters.GetSelectedCountry]: (state) => state.data.selectedCountry || '',
  [BrokerGetters.GetDocumentsForValidation]: (state) => state.data.documentsForValidation,
  [BrokerGetters.GetBookingDetails]: ({ data }) => {
    const {
      vehicleImageUrl,
      pickupOffice,
      returnOffice,
      startDateTime,
      returnDateTime,
      reservationId: bookingId,
      alreadyValidated: isAlreadyValidated,
      channel,
      pickupCountryCode,
      dropoffCountryCode,
    } = data;

    return {
      vehicleImageUrl,
      pickupOffice,
      returnOffice,
      startDateTime,
      returnDateTime,
      bookingId,
      isAlreadyValidated,
      channel,
      pickupCountryCode,
      dropoffCountryCode,
    };
  },
  [BrokerGetters.IsBookingReady]: ({ data }) => {
    const { vehicleImageUrl, pickupOffice, returnOffice, startDateTime, returnDateTime } = data;

    return every({
      vehicleImageUrl,
      pickupOffice,
      returnOffice,
      startDateTime,
      returnDateTime,
    });
  },
} as const;
